import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Catches orphaned parent accounts and routes them to the recovery screen.
 *
 * "Orphaned" = a Firebase Auth user exists with accountType:'parent' on their
 * Firestore user doc but parentalConsentVerified !== true. This happens when
 * the user closed the tab (or otherwise interrupted) between the
 * createUserWithEmailAndPassword call and verifyParentWithCardFn returning
 * success. The Stripe auth+void didn't complete, so the account is unusable.
 *
 * The mid-signup case is distinguished from the orphaned case via a
 * sessionStorage flag set by ParentStep3.handleSubmit before Auth-account
 * creation and cleared on successful VPC. If the flag is present, the user is
 * actively completing signup right now — don't sign them out. If absent
 * AND the doc shows unverified parent, they're returning to a half-formed
 * account from a prior session — sign out + route to recovery.
 *
 * Mount once inside AuthProvider (App.tsx). Idempotent per uid via a ref
 * guard so we don't re-evaluate on every render.
 */
export const PARENT_SIGNUP_IN_PROGRESS_KEY = 'db_parent_signup_in_progress';

export default function ParentSignupGuard() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handledForUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userProfile?.uid) return;
    if (userProfile.accountType !== 'parent') return;
    if (userProfile.parentalConsentVerified === true) return;

    // Mid-signup window — ParentStep3 sets this flag before Auth creation and
    // clears it on VPC success. Skip while present.
    if (sessionStorage.getItem(PARENT_SIGNUP_IN_PROGRESS_KEY) === 'true') return;

    // Avoid an infinite loop if the user is already on the recovery screen.
    if (location.pathname === '/parent-signup-recovery') return;

    // Idempotency — don't re-fire for the same uid even if the effect re-runs
    // before logout settles.
    if (handledForUidRef.current === userProfile.uid) return;
    handledForUidRef.current = userProfile.uid;

    (async () => {
      try {
        await logout();
      } catch (err) {
        console.warn('[ParentSignupGuard] logout failed', err);
      }
      navigate('/parent-signup-recovery', { replace: true });
    })();
  }, [
    userProfile?.uid,
    userProfile?.accountType,
    userProfile?.parentalConsentVerified,
    logout,
    navigate,
    location.pathname,
  ]);

  return null;
}
